import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, Label } from "@/components/site/Reveal";
import { FeatureGrid, ProcessSteps } from "@/components/site/ServiceBlocks";
import { FaqSection } from "@/components/site/Faq";
import { ldScript, serviceSchema, faqSchema } from "@/lib/schema";
import roofImg from "@/assets/svc-roof-letters.jpg";

const ROOF_LETTERS_URL = "https://www.roofletters.com";

const FAQS = [
  {
    q: "What is a roof letter?",
    a: "A roof letter is a signed and sealed engineering letter documenting the condition, construction, or remaining service life of a roof. Insurers, lenders, buyers, and building departments all request them for different reasons.",
  },
  {
    q: "Who typically needs one?",
    a: "Homeowners and property owners renewing or binding insurance, real estate transactions at closing, contractors documenting work, and owners responding to a carrier's inspection findings.",
  },
  {
    q: "Do you need to visit the property?",
    a: "It depends on the question being answered. Some letters can be issued from detailed photos and documentation, while others require an on-site inspection by a licensed professional.",
  },
  {
    q: "How fast can a letter be issued?",
    a: "Most letters are turned quickly once the inspection or photo documentation is complete. Timelines depend on scheduling the site visit and the complexity of the finding.",
  },
];

export const Route = createFileRoute("/roof-letters")({
  head: () => ({
    ...pageHead({
      title: "Roof Certification Letters · SunSmart Engineering",
      description:
        "Signed and sealed roof letters for insurance, real estate closings and permitting. Order roof certification letters online at RoofLetters.com.",
      path: "/roof-letters",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Roof Letters",
          description:
            "Signed and sealed roof certification and verification letters for insurance carriers, real estate closings, and building departments.",
          url: "/roof-letters",
          serviceType: "Inspection Services",
        }),
      ),
      ldScript(faqSchema(FAQS)),
    ],
  }),
  component: RoofLettersPage,
});

function RoofLettersPage() {
  return (
    <PageLayout
      kicker="Construction Services · Roof Letters"
      title="Roof Letters"
      intro="When a carrier, lender, or building department asks for documentation on a roof, they want it in writing from a licensed professional. RoofLetters.com is our dedicated roof-letter division. We inspect, evaluate, and issue signed and sealed roof certification and verification letters that answer the exact question being asked."
    >
      <ProcessSteps
        label="01"
        title={
          <>
            Who needs a <span className="text-sun">roof letter.</span>
          </>
        }
        image={roofImg}
        imageAlt="Roof inspector documenting a residential roof"
        steps={[
          {
            title: "Insurance Renewals & Binding",
            description:
              "Carriers frequently require documentation of roof condition and remaining service life before they will write or renew a policy.",
          },
          {
            title: "Real Estate Closings",
            description:
              "Buyers, sellers, and lenders use a roof letter to resolve inspection findings and keep a closing on schedule.",
          },
          {
            title: "Permitting & Inspections",
            description:
              "Building departments accept engineering letters to document existing construction, attachment, or repairs on the record.",
          },
          {
            title: "Contractors & Property Managers",
            description:
              "Third-party verification of completed work, storm damage, or the condition of a roof across a portfolio of properties.",
          },
        ]}
      />

      <FeatureGrid
        label="02"
        theme="surface"
        title={
          <>
            What the letter <span className="text-sun">covers.</span>
          </>
        }
        columns={4}
        items={[
          { title: "Roof Condition", description: "Observed condition of the covering, flashing, penetrations, and visible drainage." },
          { title: "Remaining Service Life", description: "A professional opinion on expected remaining life based on the observed condition and system type." },
          { title: "Construction & Attachment", description: "Documentation of the roof assembly and its attachment where that is the question at hand." },
          { title: "Signed & Sealed", description: "Issued by a licensed professional in a format carriers, lenders, and reviewers accept." },
        ]}
      />

      <PageSection label="03" title="Order Online at RoofLetters.com">
        <div className="grid gap-16 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-8">
            <p className="text-lg leading-relaxed text-muted-foreground">
              RoofLetters.com is the fastest way to request a roof letter from SunSmart. Submit the
              property details and documentation online, and the request routes straight to our
              engineering team for scheduling and issuance. For portfolios, recurring work, or
              anything outside a standard request, contact us directly and we will scope it with
              you.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="lg:col-span-4 lg:text-right">
              <a
                href={ROOF_LETTERS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink transition-transform hover:scale-[1.03]"
              >
                Order at RoofLetters.com <span aria-hidden>→</span>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 border-t border-border pt-10">
          <Label n="→" text="Related Services" />
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/verification-letters-inspections"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-sun hover:text-sun"
            >
              Verification Letters & Inspections
            </Link>
            <Link
              to="/windows-doors-roofing-exteriors"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-sun hover:text-sun"
            >
              Windows, Doors, Roofing & Exteriors
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-sun hover:text-sun"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </PageSection>

      <FaqSection
        items={FAQS}
        title={
          <>
            Roof letter <span className="text-sun">questions.</span>
          </>
        }
      />
    </PageLayout>
  );
}
